export const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', (carousel) => {
      carousel.container.setAttribute(`data-initialised`, true)
    })
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
export const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', maxIdx === 0)
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', maxIdx === 0)
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
export const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/**
 * @param {{
 *  dots?: HTMLButtonElement[],
 *  indicator?: HTMLElement,
 * }} elements
 */
export const useDots = ({ dots = [], indicator } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      const currentIndex = carousel.track.details.rel

      dots.forEach((dot, index) => {
        const isCurrent = currentIndex === index

        dot.setAttribute('aria-current', String(isCurrent))
      })

      indicator?.style.setProperty('--Progress', currentIndex)
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

/**
 * @param {HTMLElement} element
 * @param {{
 *  format: string
 * }} options
 */
export const usePagination = (
  pagination,
  { format = '[current]/[total]' } = {},
) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updatePaginationState = () => {
      const currentIndex = carousel.track.details.rel + 1
      const total = carousel.track.details.slides.length

      const string = format
        .replaceAll('[current]', currentIndex)
        .replaceAll('[total]', total)

      pagination.innerText = string
    }

    carousel.on('created', updatePaginationState)
    carousel.on('slideChanged', updatePaginationState)
  }
}

/**
 * Adds wheel / trackpad controls to a Keen carousel by translating wheel input
 * into Keen's drag events.
 *
 * Options:
 * - element: optional element to listen for wheel input on.
 * - sensitivity: drag distance multiplier. 1 is closest to native drag distance.
 * - maxDelta: caps large wheel spikes so the carousel does not jump too far.
 * - axisThreshold: how clearly the wheel gesture must match the carousel axis.
 * - dragEndDelay: delay after the last wheel event before releasing the drag.
 * - lineHeight: fallback multiplier for browsers that report wheel deltas in lines.
 * - invert: vertical scroll is converted to horizontal and vice versa.
 */
export const addWheelControls = (
  direction,
  {
    element = null,
    sensitivity = 1,
    maxDelta = 40,
    axisThreshold = 1.2,
    dragEndDelay = 150,
    lineHeight = 16,
    invert = false,
  } = {},
) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    const isHorizontal = direction === 'horizontal'
    const controlElement = element || carousel.container

    controlElement?.style?.setProperty(
      direction === 'horizontal'
        ? invert
          ? 'overscroll-behavior-y'
          : 'overscroll-behavior-x'
        : invert
          ? 'overscroll-behavior-x'
          : 'overscroll-behavior-y',
      'contain',
    )

    const clamp = (value, min, max) => {
      return Math.min(Math.max(value, min), max)
    }

    const normaliseDelta = (event) => {
      const multiplier =
        event.deltaMode === 1
          ? lineHeight
          : event.deltaMode === 2
            ? window.innerHeight
            : 1

      const rawDelta = isHorizontal
        ? invert
          ? event.deltaY
          : event.deltaX
        : invert
          ? event.deltaX
          : event.deltaY

      return clamp(rawDelta * multiplier, -maxDelta, maxDelta)
    }

    const hasCorrectScrollDirection = (event) => {
      const absX = Math.abs(event.deltaX)
      const absY = Math.abs(event.deltaY)

      return isHorizontal
        ? absX > absY * axisThreshold
        : absY > absX * axisThreshold
    }

    const dispatch = (event, name) => {
      const delta = normaliseDelta(event)

      if (isHorizontal) {
        position.x -= delta * sensitivity
      } else {
        position.y -= delta * sensitivity
      }

      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = (event) => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }

      carousel.container.dispatchEvent(
        new CustomEvent('ksDragStart', {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheel = (event) => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = () => {
      carousel.container.dispatchEvent(
        new CustomEvent('ksDragEnd', {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const eventWheel = (event) => {
      let supportWheel = true
      let earlyReturn = false
      const { details } = carousel?.track

      if (!hasCorrectScrollDirection(event) && !invert) {
        supportWheel = false
        earlyReturn = true
      }

      if (!earlyReturn && !carousel.options?.loop) {
        const scrollIndex = normaliseDelta(event)

        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)
          wheelActive = false
          wheelEnd()
        }

        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }

      wheel(event)

      clearTimeout(touchTimeout)
      touchTimeout = setTimeout(() => {
        wheelActive = false
        wheelEnd()
      }, dragEndDelay)
    }

    carousel.on('created', () => {
      controlElement.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })
  }
}

/** @param {HTMLElement} scrollbar */
export const useScrollbar = (scrollbar) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateScrollbar = () => {
      let numberOfSlidesShown = 0

      const numberOfSlides = carousel.track.details.slides.length
      const currentIndex = carousel.track.details.rel

      if (numberOfSlides <= 1) {
        scrollbar.parentElement.remove()
      }

      carousel.track.details.slides.forEach((slide) => {
        numberOfSlidesShown = numberOfSlidesShown + slide.portion
      })

      const scrollbarWidthPercentage =
        (numberOfSlidesShown / numberOfSlides) * 100

      const spaceToMove = 100 - scrollbarWidthPercentage

      let currentScrollPercentage = (currentIndex / numberOfSlides) * 100

      if (currentScrollPercentage > spaceToMove) {
        currentScrollPercentage = spaceToMove
      }

      if (scrollbar && scrollbar.style) {
        scrollbar.style.inlineSize = `${scrollbarWidthPercentage}%`
        scrollbar.style.insetInlineStart = `${currentScrollPercentage}%`
      }
    }

    carousel.on('created', updateScrollbar)
    carousel.on('slideChanged', updateScrollbar)
  }
}
